import React, { useState, useCallback, useRef } from 'react';
import '../css/selectMediaOverlay.css';
import CloseButton from '../../../button-fs-cp/close-button-op/ts/closeButton';
import { Button } from '@core/uilibrary';
import MediaCard from '../../../card-fs-cp/media-card-op/ts/mediaCard';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MediaItem {
  id:        number;
  src:       string;
  alt?:      string;
  title?:    string;
  size?:     string;
  isActive?: boolean;
}

export interface SelectMediaOverlayProps {
  /** List of media items to display */
  items:            MediaItem[];
  /** Index of the initially selected item (-1 = none) */
  defaultSelected?: number;
  /** Called with the selected item when Save is clicked */
  onSave:           (item: MediaItem | null) => void;
  /** Called when Cancel or × is clicked */
  onClose:          () => void;
  /** Whether more items can be loaded (enables infinite scroll) */
  hasMore?:         boolean;
  /** Whether a load-more request is in flight */
  loadingMore?:     boolean;
  /** Called when the user scrolls near the bottom of the grid */
  onLoadMore?:      () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SelectMediaOverlay: React.FC<SelectMediaOverlayProps> = ({
  items,
  defaultSelected = -1,
  onSave,
  onClose,
  hasMore     = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const [selectedIdx, setSelectedIdx] = useState<number>(defaultSelected);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const handleSave = () => {
    onSave(selectedIdx >= 0 ? items[selectedIdx] : null);
  };

  // ── Scroll handler on the body div — triggers onLoadMore near bottom ────────
  const handleBodyScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const el = e.currentTarget;
    /* istanbul ignore next */
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, loadingMore]);

  return (
    /* ── Backdrop ── */
    <div //NOSONAR
      className="scl--cms-components-selectMediaOverlay-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Select media"
    >
      {/* ── Modal ── */}
      <div className="scl--cms-components-selectMediaOverlay-modal">

        {/* ── Header ── */}
        <div className="scl--cms-components-selectMediaOverlay-header">
          <h2 className="scl--cms-components-selectMediaOverlay-title">Select media</h2>
          <CloseButton onClick={onClose} aria-label="Close" />
        </div>

        {/* ── Body: media card grid ── */}
        <div
          ref={bodyRef}
          className="scl--cms-components-selectMediaOverlay-body"
          onScroll={handleBodyScroll}
        >
          <div className="scl--cms-components-selectMediaOverlay-grid">
            {items.map((item, idx) => {
              const isSelected = idx === selectedIdx;

              return (
                <div //NOSONAR
                  key={item.id}
                  className={`scl--cms-components-selectMediaOverlay-card-wrapper${isSelected ? ' scl--cms-components-selectMediaOverlay-card-wrapper--selected' : ''}`}
                  onClick={() => setSelectedIdx(idx === selectedIdx ? -1 : idx)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  aria-label={item.alt ?? `Media item ${idx + 1}`}
                  onKeyDown={ /* istanbul ignore next */e => (e.key === 'Enter' || e.key === ' ') && setSelectedIdx(idx)}
                >
                  <MediaCard
                    imageSrc={item.src}
                    title={item.title ?? /* istanbul ignore next */ ''}
                    size={item.size ?? /* istanbul ignore next */ ''}
                    isActive={item.isActive ?? false}
                  />

                  {/* Selected pink tint overlay */}
                  {isSelected && (
                    <div
                      className="scl--cms-components-selectMediaOverlay-card-selected-overlay"
                      aria-hidden="true"
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Load-more indicator ── */}
          {loadingMore && (
            <div className="scl--cms-components-selectMediaOverlay-loading-more">
              Loading...
            </div>
          )}
          {!hasMore && items.length > 0 && !loadingMore && (
            <div className="scl--cms-components-selectMediaOverlay-no-more">
              No more media
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="scl--cms-components-selectMediaOverlay-footer">
          <div className="scl--cms-components-selectMediaOverlay-footer-actions">
            <Button variant="ghost"   value="Cancel" onClick={onClose}    type="button" />
            <Button variant="primary" value="Save"   onClick={handleSave} type="button" />
          </div>
        </div>

      </div>
    </div>
  );
};

export default SelectMediaOverlay;